import { InputBase, withStyles } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Popover from '@material-ui/core/Popover';
import AddIcon from '@material-ui/icons/Add';
import { debounce } from 'debounce';
import { useCombobox } from 'downshift';
import React, { useCallback, useRef } from 'react';
import { PulseLoader } from 'react-spinners';
import styled, { css } from 'styled-components';
import ArrowDown from 'utils/images/arrow-down.svg';

const StyledInputLabel = withStyles({
  formControl: {
    position: 'relative',
    transform: 'translate(0, 1.5px) scale(1)',
  },
  shrink: {
    fontSize: '10px !important',
    transform: 'translate(0, 1.5px) scale(1)',
  },
})(InputLabel);

const StyledInput = withStyles({
  root: {
    width: '77%',
  },
  input: {
    padding: '0',
    '&&': {
      lineHeight: '19px !important',
    },
  },
})(InputBase);

const StyledButton = styled.button`
  border: none;
  background-color: #ffffff;
  margin: 2px;
  padding: 0;

  ${props => css`
    cursor: ${props.disabled ? 'default' : 'pointer'};
  `}
`;

const LoaderContainer = styled.div`
  margin: 2px;
  padding: 0;
`;

const InputLabelContainer = styled.div`
  display: flex;
  align-items: center;
  min-height: 14px;
`;

const InputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 200px;
  position: relative;
  margin-top: 11px;
  height: 41px;
  border: 1px solid #dddddd;
  border-radius: 49px;
  padding: 10px 12px;
  font-family: 'Basier Circle';
  font-size: 13px;

  &:focus-within {
    border-color: #000000;
  }
`;

const ListContainer = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 8px 0;
  width: 200px;
`;

const ListItem = styled.li`
  padding: 11px 16px;
  font-weight: 400;
  font-family: 'Geomanist';

  ${props =>
    props.highlighted &&
    css`
      background-color: rgba(0, 0, 0, 0.14);
    `}

  ${props =>
    props.disabled &&
    css`
      color: #797979;
    `}
`;

const StyledPopover = styled(Popover)`
  max-height: 300px;
`;

const CrossIcon = withStyles({
  root: {
    color: '#000',
    transform: 'rotate(45deg)',
    fontSize: '20px',
    marginTop: '-1px',
  },
})(AddIcon);

const ComboInputField = ({
  label,
  labelEndAdornment,
  input,
  items = [],
  forcedItem,
  isInputDisabled,
  isItemDisabled,
  isLoading = false,
  noItemsMessage = 'No Items',
  noItemSelectedMessage = '',
  onInputEdit,
  ...custom
}) => {
  const { onChange: onInputChange, value: inputValue, ...inputProps } = input;
  const inputContainerRef = useRef(null);

  const initialSelectedItem = items.find(i => i.id === inputValue);

  const hasItems = items.length > 0;
  const hasForcedItem = forcedItem !== undefined;
  const hasInputValue = inputValue && inputValue !== '';
  const inputDisabled = isLoading || isInputDisabled || hasForcedItem;
  const showButtons = !inputDisabled;

  const getInputPlaceholder = () => {
    if (hasForcedItem) {
      return forcedItem.name;
    }

    if (!hasItems && !isLoading && isInputDisabled) {
      return noItemsMessage;
    }

    return noItemSelectedMessage;
  };

  const getHiddenInputValue = () => {
    if (hasForcedItem) {
      return forcedItem.id;
    }

    return input.value;
  };

  const itemDisabled =
    typeof isItemDisabled === 'function' ? isItemDisabled : () => false;
  const itemToString = item => (item ? item.name : '');
  const debouncedOnInputEdit = useCallback(
    debounce((value, skip) => {
      if (skip) {
        return;
      }

      if (onInputEdit && typeof onInputEdit === 'function') {
        onInputEdit(value);
      }
    }, 1000),
    []
  );

  const {
    isOpen,
    highlightedIndex,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
    openMenu,
    selectItem,
  } = useCombobox({
    items,
    itemToString,
    initialSelectedItem,
    onInputValueChange: ({ inputValue }) => {
      const matchingItem = items.find(i => i.name === inputValue);
      if (matchingItem) {
        debouncedOnInputEdit(undefined, true);
        onInputChange(matchingItem.id);
      } else {
        debouncedOnInputEdit(inputValue);
        onInputChange('');
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) {
        return;
      }

      onInputChange(selectedItem.id);
    },
  });

  const onInputFocus = () => {
    if (!isOpen) {
      openMenu();
    }
  };

  return (
    <FormControl
      className={custom.className}
      fullWidth={custom.fullWidth}
      style={{ marginTop: 16 }}
    >
      <InputLabelContainer>
        <StyledInputLabel
          {...getLabelProps()}
          shrink={custom.shrinkLabel || false}
        >
          {label}
        </StyledInputLabel>
        {labelEndAdornment}
      </InputLabelContainer>

      <InputContainer {...getComboboxProps({ ref: inputContainerRef })}>
        <StyledInput
          disabled={inputDisabled}
          {...getInputProps({
            refKey: 'inputRef',
            onFocus: onInputFocus,
            placeholder: getInputPlaceholder(),
          })}
        />
        <input type="hidden" {...inputProps} value={getHiddenInputValue()} />

        {isLoading && (
          <LoaderContainer>
            <PulseLoader size={4} />
          </LoaderContainer>
        )}

        {showButtons && hasInputValue && (
          <StyledButton
            type="button"
            disabled={inputDisabled}
            onClick={() => selectItem(null)}
          >
            <CrossIcon />
          </StyledButton>
        )}

        {showButtons && (
          <StyledButton
            type="button"
            disabled={inputDisabled}
            aria-label="toggle menu"
            {...getToggleButtonProps()}
          >
            <img alt="" src={ArrowDown} />
          </StyledButton>
        )}
      </InputContainer>

      <StyledPopover
        open={isOpen && hasItems && !isLoading}
        disableAutoFocus={true}
        disableRestoreFocus={true}
        anchorEl={inputContainerRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <ListContainer {...getMenuProps({}, { suppressRefError: true })}>
          {items.map((item, index) => (
            <ListItem
              highlighted={highlightedIndex === index}
              key={`${itemToString(item)}${index}`}
              {...getItemProps({ item, index, disabled: itemDisabled(item) })}
            >
              {itemToString(item)}
            </ListItem>
          ))}
        </ListContainer>
      </StyledPopover>
    </FormControl>
  );
};

export default ComboInputField;



// WEBPACK FOOTER //
// ./src/components/FormElements/ComboInputField.jsx